import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  BookOpen,
  Brain,
  CheckCircle2,
  Clock,
  Cpu,
  Database,
  Eye,
  FileText,
  GitBranch,
  Home,
  Layers,
  List,
  Network,
  PlayCircle,
  Radio,
  Scale,
  Shield,
  Sparkles,
  Terminal,
  Wifi,
  Zap,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { ViewKey } from '../../lib/view-keys';

interface NavItem {
  key: ViewKey;
  label: string;
  agentLabel: string;
  opcode: string;
  Icon: typeof Home;
  section: 'core' | 'tools' | 'system';
}

const NAV: NavItem[] = [
  { key: 'dashboard', label: 'Home', agentLabel: 'corpus.overview', opcode: '0x00', Icon: Home, section: 'core' },
  { key: 'data', label: 'Data Entry', agentLabel: 'corpus.ingest', opcode: '0x01', Icon: Database, section: 'core' },
  { key: 'review', label: 'Review', agentLabel: 'review.player', opcode: '0x02', Icon: PlayCircle, section: 'tools' },
];

interface AgentNavProps {
  view: ViewKey;
  onViewChange: (v: ViewKey) => void;
  sessionLabel?: string;
}

export function AgentNav({ view, onViewChange, sessionLabel }: AgentNavProps) {
  const [uptime] = useState(() => Date.now());
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const i = setInterval(() => setTick((t) => t + 1), 5000);
    return () => clearInterval(i);
  }, []);

  const uptimeSec = Math.round((Date.now() - uptime) / 1000);
  void tick;
  const uptimeStr = uptimeSec < 60 ? `${uptimeSec}s` : `${Math.floor(uptimeSec / 60)}m`;

  const sections: { label: string; items: NavItem[] }[] = [
    { label: 'CORE', items: NAV.filter((n) => n.section === 'core') },
    { label: 'TOOLS', items: NAV.filter((n) => n.section === 'tools') },
    { label: 'SYSTEM', items: NAV.filter((n) => n.section === 'system') },
  ];

  return (
    <aside className="fixed top-0 left-0 bottom-0 z-40 w-64 border-r border-white/[0.06] flex flex-col bg-[#030609] overflow-y-auto overflow-x-hidden">
      <div className="flex h-full flex-col" style={{ paddingTop: 97 }}>
        <div className="px-5 py-3 border-b border-white/[0.06] shrink-0 mt-1">
          <div className="grid grid-cols-3 gap-1.5 text-[8px] font-mono">
            <StatusChip label="NET" value="OK" tone="green" />
            <StatusChip label="RPC" value="OPEN" tone="green" />
            <StatusChip label="UP" value={uptimeStr} tone="blue" />
          </div>
        </div>

        <AgentTasksProgress />

        <div className="px-4 py-2.5 border-b border-white/[0.06] shrink-0">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[8px] font-mono tracking-[0.12em] uppercase text-slate-400 font-semibold flex items-center gap-1.5">
              <Radio size={8} className="text-[#00FFFF]" /> Swarm Topology
            </span>
            <span className="text-[8px] font-mono text-[#39FF14]">3/5 online</span>
          </div>
          <div className="space-y-1">
            <SwarmNode id="orch_0" role="orchestrator" state="active" isSelf />
            <SwarmNode id="eval_1" role="evaluator" state="active" />
            <SwarmNode id="ingest_2" role="ingest_worker" state="active" />
            <SwarmNode id="wh_dist" role="webhook_relay" state="idle" />
            <SwarmNode id="align_3" role="alignment" state="idle" />
          </div>
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto scrollbar-thin py-2">
          {sections.map((section) => (
            <div key={section.label} className="mb-1">
              <div className="px-4 py-1.5 text-[8px] font-mono tracking-[0.14em] uppercase text-slate-500 font-semibold">
                {section.label}
              </div>
              {section.items.map((item) => {
                const active = view === item.key;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => onViewChange(item.key)}
                    className={`group w-full flex items-center gap-2.5 px-4 py-2 text-[11px] font-mono transition-all duration-200 ${
                      active
                        ? 'bg-[#00FFFF]/[0.06] text-slate-100 border-l-2 border-l-[#00FFFF]'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-white/[0.03] border-l-2 border-l-transparent'
                    }`}
                  >
                    <item.Icon
                      size={13}
                      className={`transition-colors shrink-0 ${active ? 'text-[#00FFFF]' : 'text-slate-500 group-hover:text-slate-300'}`}
                    />
                    <span className="flex-1 text-left truncate">{item.agentLabel}</span>
                    <span className={`text-[8px] tabular-nums ${active ? 'text-[#00FFFF]/70' : 'text-slate-600'}`}>
                      {item.opcode}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}

          <div className="mb-1">
            <div className="px-4 py-1.5 text-[8px] font-mono tracking-[0.14em] uppercase text-slate-500 font-semibold">
              AGENT
            </div>
            <AgentQuickLink icon={<Terminal size={13} />} label="control_plane" hint="cmd" />
            <AgentQuickLink icon={<Layers size={13} />} label="plan.decompose" hint="plan" />
            <AgentQuickLink icon={<Network size={13} />} label="connector.registry" hint="api" />
            <AgentQuickLink icon={<Zap size={13} />} label="async.queue" hint="task" />
          </div>

          <div className="mb-1">
            <div className="px-4 py-1.5 text-[8px] font-mono tracking-[0.14em] uppercase text-slate-500 font-semibold">
              REASONING
            </div>
            <AgentQuickLink icon={<Brain size={13} />} label="chain_of_thought" hint="cot" />
            <AgentQuickLink icon={<Eye size={13} />} label="introspect.state" hint="self" />
            <AgentQuickLink icon={<Scale size={13} />} label="eval.criteria" hint="eval" />
            <AgentQuickLink icon={<Sparkles size={13} />} label="hypothesis.test" hint="hyp" />
          </div>

          <div className="mb-1">
            <div className="px-4 py-1.5 text-[8px] font-mono tracking-[0.14em] uppercase text-slate-500 font-semibold">
              ORCHESTRATION
            </div>
            <AgentQuickLink icon={<List size={13} />} label="batch.orchestrate" hint="batch" />
            <AgentQuickLink icon={<Clock size={13} />} label="temporal.schedule" hint="cron" />
            <AgentQuickLink icon={<GitBranch size={13} />} label="dag.execute" hint="dag" />
            <AgentQuickLink icon={<Radio size={13} />} label="event.stream" hint="sse" />
          </div>

          <div className="mb-1">
            <div className="px-4 py-1.5 text-[8px] font-mono tracking-[0.14em] uppercase text-slate-500 font-semibold">
              PROVENANCE
            </div>
            <AgentQuickLink icon={<FileText size={13} />} label="audit.log" hint="log" />
            <AgentQuickLink icon={<BookOpen size={13} />} label="lineage.trace" hint="trace" />
            <AgentQuickLink icon={<Shield size={13} />} label="policy.engine" hint="rls" />
          </div>
        </nav>

        <div className="px-4 py-2.5 border-t border-white/[0.06] shrink-0">
          <div className="flex items-center gap-2 mb-2">
            <Shield size={10} className="text-[#00FFFF]" />
            <span className="text-[8px] font-mono tracking-[0.12em] uppercase text-slate-400 font-semibold">
              Capabilities
            </span>
          </div>
          <div className="flex flex-wrap gap-1">
            <CapBadge label="NL" title="Natural Language" />
            <CapBadge label="FN" title="Function Calls" />
            <CapBadge label="PIPE" title="Pipelines" />
            <CapBadge label="PLAN" title="Plan Decomposition" />
            <CapBadge label="MEM" title="Working Memory" />
            <CapBadge label="COT" title="Chain-of-Thought" />
            <CapBadge label="DAG" title="DAG Execution" />
            <CapBadge label="EVAL" title="Evaluation" />
            <CapBadge label="SWARM" title="Swarm Protocol" />
          </div>
        </div>

        <div className="px-4 py-2.5 border-t border-white/[0.06] shrink-0">
          <div className="flex items-center gap-2">
            <Cpu size={10} className="text-slate-500" />
            <div className="min-w-0 flex-1">
              <div className="text-[8px] font-mono text-slate-500 tracking-[0.08em] uppercase">Session</div>
              <div className="text-[9px] font-mono text-slate-300 truncate">{sessionLabel ?? 'anon'}</div>
            </div>
            <div className="flex items-center gap-1">
              <Wifi size={9} className="text-[#39FF14]" />
              <GitBranch size={9} className="text-slate-500" />
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}

function AgentTasksProgress() {
  const [completed, setCompleted] = useState(0);
  const [total, setTotal] = useState(0);
  const [displayCompleted, setDisplayCompleted] = useState(0);
  const animRef = useRef(0);

  const fetchCounts = useCallback(async () => {
    try {
      const { count: totalCount } = await supabase
        .from('agent_work_items')
        .select('*', { count: 'exact', head: true });
      const { count: doneCount } = await supabase
        .from('agent_work_items')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'approved');
      const t = totalCount ?? 10;
      const c = doneCount ?? 3;
      setTotal(t);
      setCompleted((prev) => (c !== prev ? c : prev));
    } catch {
      setTotal(10);
      setCompleted(3);
    }
  }, []);

  useEffect(() => {
    void fetchCounts();
    const interval = setInterval(() => void fetchCounts(), 5000);
    return () => clearInterval(interval);
  }, [fetchCounts]);

  useEffect(() => {
    if (displayCompleted === completed) return;
    const step = completed > displayCompleted ? 1 : -1;
    const speed = Math.max(30, 200 / Math.abs(completed - displayCompleted));
    animRef.current = window.setTimeout(() => {
      setDisplayCompleted((prev) => prev + step);
    }, speed);
    return () => clearTimeout(animRef.current);
  }, [completed, displayCompleted]);

  const pct = total > 0 ? Math.round((displayCompleted / total) * 100) : 0;

  return (
    <div className="px-4 py-3 border-b border-white/[0.06] shrink-0">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[8px] font-mono tracking-[0.12em] uppercase text-slate-400 font-semibold flex items-center gap-1.5">
          <CheckCircle2 size={9} className="text-[#39FF14]" /> Agent Tasks
        </span>
        <span className="text-[9px] font-mono text-[#39FF14] tabular-nums font-bold">
          {displayCompleted}/{total}
        </span>
      </div>
      <div className="relative h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#39FF14] to-[#00FFFF] transition-[width] duration-700 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-[8px] font-mono text-slate-500">{pct}% complete</span>
        <span className="text-[8px] font-mono text-slate-600">{total - displayCompleted} in queue</span>
      </div>
    </div>
  );
}

function StatusChip({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'green' | 'blue' | 'amber' | 'red';
}) {
  const colors = {
    green: 'text-[#39FF14] bg-[#39FF14]/[0.08]',
    blue: 'text-[#00FFFF] bg-[#00FFFF]/[0.08]',
    amber: 'text-[#FFD700] bg-[#FFD700]/[0.08]',
    red: 'text-[#FF003C] bg-[#FF003C]/[0.08]',
  };
  return (
    <div className={`flex items-center justify-between px-1.5 py-0.5 rounded ${colors[tone]}`}>
      <span className="text-slate-400">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}

function SwarmNode({
  id,
  role,
  state,
  isSelf,
}: {
  id: string;
  role: string;
  state: 'active' | 'idle' | 'error';
  isSelf?: boolean;
}) {
  const stateColor =
    state === 'active'
      ? 'bg-[#00FFFF] shadow-[0_0_4px_#00FFFF]'
      : state === 'idle'
        ? 'bg-slate-600'
        : 'bg-[#FF003C]';
  return (
    <div className="flex items-center gap-2 text-[9px] font-mono">
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${stateColor} ${state === 'active' ? 'animate-pulse' : ''}`} />
      <span className={`${isSelf ? 'text-[#00FFFF]' : 'text-slate-400'}`}>{id}</span>
      <span className="text-slate-600 ml-auto">{role}</span>
    </div>
  );
}

function AgentQuickLink({ icon, label, hint }: { icon: React.ReactNode; label: string; hint: string }) {
  return (
    <div className="flex items-center gap-2.5 px-4 py-1.5 text-[10px] font-mono cursor-default text-slate-500">
      <span className="text-slate-600">{icon}</span>
      <span className="flex-1 truncate">{label}</span>
      <span className="text-[8px] text-slate-600 bg-white/[0.03] px-1 py-0.5 rounded">{hint}</span>
    </div>
  );
}

function CapBadge({ label, title }: { label: string; title: string }) {
  return (
    <span
      title={title}
      className="text-[7px] font-mono font-bold tracking-[0.1em] uppercase text-[#00FFFF]/80 bg-[#00FFFF]/[0.06] px-1.5 py-0.5 rounded cursor-default"
    >
      {label}
    </span>
  );
}

export default AgentNav;
